<template>
    <header
        ref="header"
        class="fixed left-0 w-full"
        :class="dark ? 'bg-black bg-theme-dark' : 'bg-white'"
        :style="`top: ${announcementsCombinedHeight}`"
        :aria-label="$t('a11y.header')"
    >
        <h2 class="offscreen">{{ $t('a11y.main_navigation') }}</h2>

        {{/* * */}}
        {{/* MOBILE - logo & hamburger button */}}
        {{/* * */}}
        <div class="py-4 container flex justify-between lg:hidden relative z-2" :class="dark ? 'bg-black' : 'bg-white'">
            <NuxtLink :to="localePath('index')" :aria-label="$t('navigation.homepage')">
                <LigetLogo class="w-40 h-8" :white="dark" />
            </NuxtLink>
            <div class="flex items-center">
                <Button
                    class="p-button-secondary p-button-text"
                    :aria-label="$t('navigation.toggle_mobile_menu')"
                    :icon="isMenuOpen ? 'pi pi-times' : 'pi pi-bars'"
                    :aria-expanded="isMenuOpen ? 'true' : 'false'"
                    @click.native="isMenuOpen = !isMenuOpen"
                />
            </div>
        </div>

        {{/* * */}}
        {{/* MOBILE & DESKTOP - the menu itself */}}
        {{/* * */}}
        <Transition name="fade-left-full" mode="out-in">
            <nav
                v-show="isMenuOpen"
                id="main-nav"
                ref="navigation"
                class="bg-gray-100 text-black"
                :class="{ 'lg:bg-white': !dark, 'lg:bg-black': dark }"
                :aria-label="$t('a11y.main_menu')"
            >
                <div class="container relative pt-4 lg:pt-0">
                    {{/* MOBILE - searchbar */}}
                    <form class="relative lg:hidden" role="search" @submit.prevent="search">
                        <InputText
                            v-model="searchQuery"
                            class="mobile-search undark"
                            :placeholder="$t('generic.search')"
                            role="searchbox"
                        />
                        <div class="absolute top-1/2 right-0 -translate-y-1/2">
                            <Button
                                type="submit"
                                class="p-button-secondary p-button-text"
                                icon="pi pi-search"
                                :aria-label="$t('generic.search')"
                            />
                        </div>
                    </form>

                    <div
                        class="flex flex-col lg:flex-row lg:items-center lg:justify-center"
                        :class="{ 'transition-panels': doMenuPanelsHaveTransition }"
                    >
                        <ul class="lvl1-menu-group lvl1-menu-group--left">
                            <li
                                id="program"
                                ref="program"
                                class="lvl1-menu-item lg:mr-6 xl:mr-9"
                                :class="{ open: isLvl2PanelOpen.program }"
                            >
                                <button
                                    data-id="program"
                                    :class="{ 'nuxt-link-active': /^programs-in-varosliget___/.test($route.name) }"
                                    :aria-label="$t('navigation.programs')"
                                    :aria-expanded="isLvl2PanelOpen.program ? 'true' : 'false'"
                                    @click="handleLvl1MenuItemClick"
                                >
                                    <span><T t="navigation.programs" /></span> <i class="pi pi-chevron-down"></i>
                                </button>

                                <div class="lvl1-menu-item__panel" :class="{ hidden: !isLvl2PanelOpen.program }">
                                    <div
                                        class="lg:container grid grid-cols-12 base-responsive-gap pb-4 lg:pb-0 items-start"
                                    >
                                        <ul
                                            v-if="$store.state.data.eventCategories.length"
                                            class="col-span-12 lg:col-span-3 grid grid-cols-1 gap-4 lg:gap-5"
                                        >
                                            <li class="lvl2-menu-item">
                                                <NuxtLink
                                                    :to="
                                                        localePath({
                                                            name: 'programs-in-varosliget',
                                                            query: {
                                                                category: EVENT_CATEGORIES.CULTURAL.slug,
                                                            },
                                                        })
                                                    "
                                                >
                                                    <span><T t="navigation.category_1_programs" /></span>
                                                </NuxtLink>
                                            </li>

                                            <li class="lvl2-menu-item">
                                                <NuxtLink
                                                    :to="
                                                        localePath({
                                                            name: 'programs-in-varosliget',
                                                            query: {
                                                                category: EVENT_CATEGORIES.OUTDOOR.slug,
                                                            },
                                                        })
                                                    "
                                                >
                                                    <span><T t="navigation.category_2_programs" /></span>
                                                </NuxtLink>
                                            </li>

                                            <li class="lvl2-menu-item">
                                                <NuxtLink
                                                    :to="
                                                        localePath({
                                                            name: 'programs-in-varosliget',
                                                            query: {
                                                                category: EVENT_CATEGORIES.FAMILY.slug,
                                                            },
                                                        })
                                                    "
                                                >
                                                    <span><T t="navigation.category_3_programs" /></span>
                                                </NuxtLink>
                                            </li>

                                            <li class="lvl2-menu-item">
                                                <NuxtLink
                                                    :to="
                                                        localePath({
                                                            name: 'programs-in-varosliget',
                                                            query: {
                                                                category: EVENT_CATEGORIES.SPORT.slug,
                                                            },
                                                        })
                                                    "
                                                >
                                                    <span><T t="navigation.category_4_programs" /></span>
                                                </NuxtLink>
                                            </li>

                                            <li role="separator" class="border-t border-black hidden lg:block"></li>

                                            <li class="lvl2-menu-item">
                                                <NuxtLink
                                                    :to="
                                                        localePath({
                                                            name: 'programs-in-varosliget',
                                                            query: {
                                                                category: EVENT_CATEGORIES.CULTURAL.slug,
                                                                from: $dayjs().format('YYYY-MM-DD'),
                                                                to: $dayjs().format('YYYY-MM-DD'),
                                                            },
                                                        })
                                                    "
                                                >
                                                    <span><T t="navigation.programs_today" /></span>
                                                </NuxtLink>
                                            </li>

                                            <li class="lvl2-menu-item">
                                                <NuxtLink
                                                    :to="
                                                        localePath({
                                                            name: 'programs-in-varosliget',
                                                            query: {
                                                                category: EVENT_CATEGORIES.CULTURAL.slug,
                                                                from: $dayjs()
                                                                    .add(7 - $dayjs().day() - 1, 'day')
                                                                    .format('YYYY-MM-DD'),
                                                                to: $dayjs()
                                                                    .add(7 - $dayjs().day(), 'day')
                                                                    .format('YYYY-MM-DD'),
                                                            },
                                                        })
                                                    "
                                                >
                                                    <span><T t="navigation.programs_this_weekend" /></span>
                                                </NuxtLink>
                                            </li>
                                        </ul>
                                        <div
                                            v-if="$store.state.data.recommendedEvents.length"
                                            class="col-span-12 lg:col-span-8 lg:col-start-5 hidden lg:block"
                                            role="group"
                                        >
                                            <h2 class="mb-5 text-sm uppercase">
                                                <T t="navigation.featured_programs" />
                                            </h2>
                                            <ul
                                                class="grid grid-cols-3 base-responsive-gap"
                                                :aria-label="$t('navigation.featured_programs')"
                                            >
                                                <li
                                                    v-for="event in $store.state.data.recommendedEvents.slice(0, 3)"
                                                    :key="`upcoming-program-${event.id}`"
                                                >
                                                    <HeaderProgramCard
                                                        :is-image-displayed="isLvl2PanelOpen.event || isMenuOpen"
                                                        :title="event.name"
                                                        :url="$getEventUrl(event)"
                                                        :date="$getFormattedEventInterval(event)"
                                                        :img="$getMediaImage(event.media, 'lead', 'sm')"
                                                    />
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </li>

                            <li
                                id="locations"
                                ref="locations"
                                class="lvl1-menu-item lg:mr-6 xl:mr-9"
                                :class="{ open: isLvl2PanelOpen.locations }"
                            >
                                <button
                                    data-id="locations"
                                    :class="{ 'nuxt-link-active': /^top-locations-/.test($route.name) }"
                                    :aria-label="$t('navigation.top_locations')"
                                    :aria-expanded="isLvl2PanelOpen.locations ? 'true' : 'false'"
                                    @click="handleLvl1MenuItemClick"
                                >
                                    <span><T t="navigation.top_locations" /></span> <i class="pi pi-chevron-down"></i>
                                </button>
                                <div class="lvl1-menu-item__panel" :class="{ hidden: !isLvl2PanelOpen.locations }">
                                    <ul
                                        class="lg:container grid grid-cols-1 lg:grid-cols-5 gap-2 lg:gap-y-10 lg:gap-x-16 pb-4 lg:pb-0"
                                    >
                                        <TopLocationMenuItemCard
                                            slug="house-of-hungarian-music"
                                            title-key="navigation.house_of_hungarian_music"
                                            static-image-key="house-of-hungarian-music___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="museum-of-ethnography"
                                            title-key="navigation.ethnography"
                                            static-image-key="ethnography___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="museum-of-fine-arts"
                                            title-key="navigation.museum_of_fine_arts"
                                            static-image-key="museum-of-fine-arts___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="house-of-the-hungarian-millennium"
                                            title-key="navigation.millennium"
                                            static-image-key="millennium___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="playground-of-varosliget"
                                            title-key="navigation.playground"
                                            static-image-key="playground___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="kresz-park"
                                            title-key="navigation.kresz_park"
                                            static-image-key="kresz-park___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="balloon-fly"
                                            title-key="navigation.balloon_fly"
                                            static-image-key="balloon-fly___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="varosliget-promenade"
                                            title-key="navigation.promenade"
                                            static-image-key="promenade___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="mocsenyi-mihaly-botanical-garden"
                                            title-key="navigation.mocsenyi"
                                            static-image-key="mocsenyi___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <TopLocationMenuItemCard
                                            slug="bird-friendly-learning-trail"
                                            title-key="navigation.bird_friendly_learning_trail"
                                            static-image-key="bird-friendly-learning-trail___hero"
                                            :is-image-displayed="isLvl2PanelOpen.locations || isMenuOpen"
                                            @anchor-click="closeMenuPanels"
                                        />
                                        <li class="col-span-1 lg:col-span-5 bg-theme-dark hidden lg:block">
                                            <Button
                                                :to="localePath('map')"
                                                :label="$t('navigation.map_button_label')"
                                            />
                                        </li>
                                    </ul>
                                </div>
                            </li>

                            <li
                                id="visiting"
                                ref="visiting"
                                class="lvl1-menu-item"
                                :class="{ open: isLvl2PanelOpen.visiting }"
                            >
                                <button
                                    data-id="visiting"
                                    :class="{ 'nuxt-link-active': /^visitor-information-/.test($route.name) }"
                                    :aria-label="$t('navigation.visitor_information')"
                                    :aria-expanded="isLvl2PanelOpen.visiting ? 'true' : 'false'"
                                    @click="handleLvl1MenuItemClick"
                                >
                                    <span><T t="navigation.visitor_information" /></span>
                                    <i class="pi pi-chevron-down"></i>
                                </button>
                                <div class="lvl1-menu-item__panel" :class="{ hidden: !isLvl2PanelOpen.visiting }">
                                    <div class="lg:container pb-4 lg:pb-0">
                                        <div class="grid grid-cols-12 base-responsive-gap">
                                            <div class="col-span-12 sm:col-span-6 lg:col-span-9">
                                                <ul
                                                    v-if="menuZones.HeaderVisitors?.menuItems.length"
                                                    class="grid grid-cols-12 lg:grid-cols-9 gap-4 lg:gap-5"
                                                >
                                                    <li
                                                        v-for="(item, i) in menuZones.HeaderVisitors.menuItems"
                                                        :key="`visitors-${item.id}`"
                                                        class="lvl2-menu-item"
                                                        :class="
                                                            i % 2 === 0
                                                                ? 'col-span-12 lg:col-span-4 lg:col-start-1'
                                                                : 'col-span-12 lg:col-span-5'
                                                        "
                                                    >
                                                        <a v-if="item.new_window" :href="item.url" target="_blank">
                                                            {{ item.title }}
                                                        </a>
                                                        <NuxtLink v-else :to="item.url">
                                                            <span>{{ item.title }}</span>
                                                        </NuxtLink>
                                                    </li>
                                                </ul>
                                            </div>

                                            <div class="col-span-12 sm:col-span-6 lg:col-span-3 p-2 lg:p-0">
                                                <NuxtLink :to="localePath('top-locations-balloon-fly')" class="block">
                                                    <div
                                                        class="aspect-w-4 aspect-h-3 base-responsive-rounding thumbnail"
                                                    >
                                                        <img
                                                            v-if="isLvl2PanelOpen.visiting"
                                                            :src="
                                                                $store.state.data.balloonStatus === 1
                                                                    ? require('@/assets/images/balloon-fly/active.jpg')
                                                                    : require('@/assets/images/balloon-fly/inactive.jpg')
                                                            "
                                                            :alt="$t('balloon_fly.title')"
                                                        />
                                                    </div>
                                                    <div class="font-Poppins font-bold text-lg mt-4">
                                                        <T t="balloon_fly.title" />
                                                    </div>
                                                    <div class="mt-2">
                                                        <T
                                                            :t="
                                                                $store.state.data.balloonStatus === 1
                                                                    ? 'balloon_fly.open'
                                                                    : 'balloon_fly.closed'
                                                            "
                                                        />
                                                    </div>
                                                </NuxtLink>
                                                <Button
                                                    tabindex="-1"
                                                    :to="
                                                        localePath({
                                                            name: 'top-locations-balloon-fly',
                                                            hash: '#schedule',
                                                        })
                                                    "
                                                    :label="$t('generic.view')"
                                                    class="mt-4"
                                                />
                                            </div>
                                            <div class="col-span-12 bg-theme-dark hidden lg:block">
                                                <Button
                                                    :to="localePath('map')"
                                                    :label="$t('navigation.map_button_label')"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>

                        {{/* DESKTOP - logo */}}
                        <NuxtLink
                            :aria-label="$t('navigation.homepage')"
                            :to="localePath('index')"
                            class="hidden lg:block relative w-40 h-8 xl:w-52 xl:h-10 -mt-1.5 xl:-mt-3"
                            @focus.native="() => closeMenuPanels()"
                        >
                            <LigetLogo class="w-40 h-8 xl:w-52 xl:h-10" :white="dark" />
                        </NuxtLink>

                        <ul class="lvl1-menu-group lvl1-menu-group--right">
                            <li class="lvl1-menu-item">
                                <NuxtLink
                                    :to="localePath('the-project')"
                                    :class="{ 'nuxt-link-active': /^the-project/.test(getRouteBaseName()) }"
                                >
                                    <span>
                                        <T t="navigation.the_project" />
                                    </span>
                                </NuxtLink>
                            </li>
                            <li class="lvl1-menu-item lg:ml-6 xl:ml-9">
                                <NuxtLink :to="localePath('magazine')">
                                    <span>
                                        <T t="navigation.news" />
                                    </span>
                                </NuxtLink>
                            </li>
                            <li
                                id="b2b"
                                ref="b2b"
                                class="lvl1-menu-item lg:ml-6 xl:ml-9"
                                :class="{ open: isLvl2PanelOpen.b2b }"
                            >
                                <button
                                    data-id="b2b"
                                    :class="{ 'nuxt-link-active': /^b2b-/.test($route.name) }"
                                    :aria-label="$t('navigation.b2b')"
                                    :aria-expanded="isLvl2PanelOpen.b2b ? 'true' : 'false'"
                                    @click="handleLvl1MenuItemClick"
                                >
                                    <span><T t="navigation.b2b" /></span>
                                    <i class="pi pi-chevron-down"></i>
                                </button>
                                <div class="lvl1-menu-item__panel" :class="{ hidden: !isLvl2PanelOpen.b2b }">
                                    <div class="lg:container pb-4 lg:pb-0">
                                        <div class="grid grid-cols-12 base-responsive-gap">
                                            <div class="col-span-12 sm:col-span-6 lg:col-span-9">
                                                <ul
                                                    v-if="menuZones.HeaderB2B?.menuItems.length"
                                                    class="grid grid-cols-12 lg:grid-cols-9 gap-4 lg:gap-5"
                                                >
                                                    <li
                                                        v-for="item in menuZones.HeaderB2B.menuItems"
                                                        :key="`visitors-${item.id}`"
                                                        class="lvl2-menu-item col-span-12 lg:col-span-4 lg:col-start-1"
                                                    >
                                                        <a v-if="item.new_window" :href="item.url" target="_blank">
                                                            {{ item.title }}
                                                        </a>
                                                        <NuxtLink v-else :to="item.url">
                                                            <span>{{ item.title }}</span>
                                                        </NuxtLink>
                                                    </li>
                                                </ul>
                                            </div>

                                            <div class="col-span-12 sm:col-span-6 lg:col-span-3 p-2 lg:p-0">
                                                <a
                                                    href="/assets/pdf/b2b_sales_guide_2024.pdf"
                                                    target="_blank"
                                                    class="block"
                                                >
                                                    <div
                                                        class="aspect-w-4 aspect-h-3 base-responsive-rounding thumbnail"
                                                    >
                                                        <img
                                                            v-if="isLvl2PanelOpen.b2b"
                                                            :src="
                                                                require('@/assets/images/illustrations/liget-bird-view.jpg')
                                                            "
                                                            :alt="$t('b2b.sales_guide')"
                                                        />
                                                    </div>
                                                    <div class="font-Poppins font-bold text-lg mt-4">
                                                        <T t="b2b.sales_guide" />
                                                    </div>
                                                </a>
                                                <Button
                                                    tabindex="-1"
                                                    to="/assets/pdf/b2b_sales_guide_2024.pdf"
                                                    target="_blank"
                                                    :label="$t('generic.view')"
                                                    class="mt-4"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>

                        {{/* MOBILE - social icons & language switcher */}}
                        <div class="flex lg:hidden items-center justify-between pt-8 pb-7">
                            <div class="flex lg:hidden items-center">
                                <SocialIcon
                                    type="instagram"
                                    small
                                    url="https://www.instagram.com/liget_budapest/"
                                    class="mx-2"
                                />
                                <SocialIcon
                                    type="facebook"
                                    small
                                    url="https://www.facebook.com/miligetunk/"
                                    class="mx-2"
                                />
                                <SocialIcon
                                    type="youtube"
                                    small
                                    url="https://www.youtube.com/channel/UCQnLn4FHzg7nj5LJ6w5voKA"
                                    class="mx-2"
                                />
                                <SocialIcon
                                    type="linkedin"
                                    small
                                    url="https://www.linkedin.com/company/75430226/"
                                    class="mx-2"
                                />
                            </div>
                            <div class="flex">
                                <NuxtLink :to="localePath('map')" class="mr-4">
                                    <MapIcon />
                                </NuxtLink>
                                <LanguageSwitcher mobile />
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </Transition>

        {{/* * */}}
        {{/* DESKTOP - language switcher & search button */}}
        {{/* * */}}
        <div class="hidden lg:flex absolute top-3 right-5 items-center z-2">
            <LanguageSwitcher class="mr-5" :theme-dark="dark" />

            <NuxtLink :to="localePath('map')" class="mr-2">
                <MapIcon />
            </NuxtLink>
            <Button
                class="p-button-secondary p-button-text"
                :aria-label="$t('generic.open_searchbar')"
                :title="$t('generic.open_searchbar')"
                icon="pi pi-search"
                @click.native="isSearchPanelOpen = true"
            />
        </div>

        {{/* * */}}
        {{/* DESKTOP - search panel */}}
        {{/* * */}}
        <Dialog header="Globális keresés" :visible.sync="isSearchPanelOpen" :dismissable-mask="true" :modal="true">
            <div class="container w-screen">
                <form class="w-full" role="search" @submit.prevent="search">
                    <div class="relative pt-4">
                        <InputText
                            id="desktop-search-input"
                            v-model="searchQuery"
                            :placeholder="$t('generic.search')"
                            class="desktop-search undark"
                            role="searchbox"
                            autofocus
                        />
                        <div class="absolute top-1/2 right-0 -translate-y-1/2">
                            <Button
                                type="submit"
                                icon="pi pi-search"
                                :aria-label="$t('generic.search')"
                                class="p-button-text p-button-secondary"
                                :class="{ 'text-white': dark }"
                            />
                        </div>
                    </div>
                </form>
            </div>

            <template #footer>
                <div class="text-center">
                    <Button :label="$t('generic.search')" :aria-label="$t('generic.search')" @click.native="search()" />
                </div>
            </template>
        </Dialog>

        {{/* * */}}
        {{/* DESKTOP - menu background overlay */}}
        {{/* * */}}
        <div
            class="fixed top-0 left-0 w-full h-full bg-overlay-transparent z-1 hidden lg:block transition-opacity duration-[300ms]"
            :class="isMenuPanelBgOverlayVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'"
        ></div>
    </header>
</template>

<script>
import InputText from 'primevue/inputtext/InputText';
import Dialog from 'primevue/dialog/Dialog.vue';
import HeaderProgramCard from '@/components/Cards/HeaderProgramCard/HeaderProgramCard.vue';
import TopLocationMenuItemCard from '@/components/Cards/TopLocationMenuItemCard/TopLocationMenuItemCard.vue';
import SocialIcon from '@/components/Generic/SocialIcon/SocialIcon.vue';
import LigetLogo from '@/components/SVGs/LigetLogo.vue';
import MapIcon from '@/components/SVGs/MapIcon.vue';
import announcement from '@/mixins/announcement.js';
import events from '@/mixins/events.js';
import LanguageSwitcher from '@/components/Layout/LanguageSwitcher.vue';

export default {
    name: 'Header',
    components: {
        LanguageSwitcher,
        SocialIcon,
        HeaderProgramCard,
        TopLocationMenuItemCard,
        InputText,
        Dialog,
        LigetLogo,
        MapIcon,
    },
    mixins: [announcement, events],
    props: {
        dark: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            searchQuery: '',
            isLanguageDropdownOpen: false,
            isLvl2PanelOpen: {
                program: false,
                locations: false,
                visiting: false,
                b2b: false,
            },
            isMenuPanelBgOverlayVisible: false,
            doMenuPanelsHaveTransition: true,
            isSearchPanelOpen: false,
            isMenuOpen: false,
        };
    },
    computed: {
        publicAnnouncementsToDisplay() {
            return this.$store.getters['data/getPublicAnnouncementsToDisplay'];
        },
        menuZones() {
            return this.$store.getters['data/getMenuZones'];
        },
    },
    watch: {
        '$i18n.locale'() {
            this.isMenuOpen = false;
            document.getElementById('tabindex-drop-item').focus();
        },
        '$store.state.windowWidth'(value) {
            if (value < 1024 && this.isSearchPanelOpen) {
                this.isSearchPanelOpen = false;
            }
        },
    },
    mounted() {
        document.querySelector('body').addEventListener('click', (e) => {
            if (!e.target.closest('.lvl1-menu-item')) {
                this.closeMenuPanels();
            }
        });

        Array.from(document.querySelectorAll('header a')).forEach((anchor) => {
            anchor.addEventListener('click', () => {
                this.doMenuPanelsHaveTransition = true;

                this.$nextTick(() => {
                    this.isMenuOpen = false;
                    this.closeMenuPanels(true);
                });
            });
        });

        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                if (window.innerWidth < 1024 && this.isMenuOpen) {
                    this.isMenuOpen = false;
                }
                this.closeMenuPanels();
            }
        });

        // setTimeout(() => {
        //     Array.from(document.querySelectorAll('*')).forEach((anchor) => {
        //         anchor.addEventListener('focus', (e) => {
        //             console.log(e.target);
        //         });
        //     });
        // }, 1500);

        Array.from(document.querySelectorAll('.lvl1-menu-item__panel a, .lvl1-menu-item__panel button')).forEach(
            (element) => {
                element.addEventListener('focus', (e) => {
                    const parentLvl1MenuItem = e.target.closest('.lvl1-menu-item');
                    const parentPanel = e.target.closest('.lvl1-menu-item__panel');
                    const panelId = parentLvl1MenuItem.getAttribute('id');

                    if (!this.isLvl2PanelOpen[panelId]) {
                        this.toggleMenuPanel(parentPanel, panelId);
                    }
                });
            }
        );
    },
    methods: {
        toggleMenuPanel(panelElement, panelId) {
            if (window.innerWidth < 1024) {
                // BEHAVIOR ON MOBILE & TABLET

                const panel = panelElement;

                if (this.isLvl2PanelOpen[panelId]) {
                    // If user clicked on an accordion header that's panel is already open, close it
                    panel.style.maxHeight = 0;
                    this.isLvl2PanelOpen[panelId] = false;
                } else {
                    // Else, first reset (close all other panels)
                    Object.keys(this.isLvl2PanelOpen).forEach((key) => {
                        this.isLvl2PanelOpen[key] = false;
                        this.$refs[key].firstElementChild.nextElementSibling.style.transitionDuration = '0s';
                        this.$refs[key].firstElementChild.nextElementSibling.style.maxHeight = 0;
                    });

                    // Then open just that panel
                    panel.style.maxHeight = 1000 + 'px';
                    panel.style.transitionDuration = '.5s';
                    this.isLvl2PanelOpen[panelId] = true;
                    setTimeout(() => {
                        if (this.isLvl2PanelOpen[panelId]) {
                            panel.style.maxHeight = 'auto';
                            panel.style.transitionDuration = `${panel.scrollHeight}ms`;
                        }
                    }, 500);
                }
            } else {
                // BEHAVIOR ON DESKTOP

                // remove if you wanna restore lvl2 panel opening on hover

                // eslint-disable-next-line
                if (this.isLvl2PanelOpen[panelId]) {
                    this.$refs[panelId].classList.remove('open');
                    this.isLvl2PanelOpen[panelId] = false;
                    this.isMenuPanelBgOverlayVisible = false;
                } else {
                    Object.keys(this.isLvl2PanelOpen).forEach((key) => {
                        this.isLvl2PanelOpen[key] = false;
                    });

                    this.$refs.program.classList.remove('open');
                    this.$refs.locations.classList.remove('open');
                    this.$refs.visiting.classList.remove('open');

                    this.isLvl2PanelOpen[panelId] = true;

                    this.isMenuPanelBgOverlayVisible = true;

                    this.$refs[panelId].classList.add('open');
                }

                setTimeout(() => {
                    if (this.isMenuPanelBgOverlayVisible) {
                        this.doMenuPanelsHaveTransition = false;
                    }
                }, 300);
            }
        },
        handleLvl1MenuItemClick(e) {
            this.toggleMenuPanel(e.target.nextElementSibling, e.target.parentElement.id);
        },
        closeMenuPanels(shouldResetTabindex = false) {
            this.doMenuPanelsHaveTransition = true;

            if (shouldResetTabindex) {
                document.getElementById('tabindex-drop-item').focus();
            }

            this.$nextTick(() => {
                if (this.$refs.program) {
                    this.$refs.program.classList.remove('open');
                }
                if (this.$refs.locations) {
                    this.$refs.locations.classList.remove('open');
                }
                if (this.$refs.visiting) {
                    this.$refs.visiting.classList.remove('open');
                }

                Object.keys(this.isLvl2PanelOpen).forEach((key) => {
                    this.isLvl2PanelOpen[key] = false;
                });

                this.isMenuPanelBgOverlayVisible = false;
            });
        },
        search() {
            const q = this.searchQuery;

            this.isMenuOpen = false;
            this.closeSearchPanel();
            this.$router.push(this.localePath({ name: 'search', query: { q } }));
        },
        closeSearchPanel() {
            this.isSearchPanelOpen = false;
            this.searchQuery = '';
        },
    },
};
</script>

<style>
#main-nav {
    @apply absolute lg:relative w-full z-2 h-[calc(100vh-72px)] lg:h-auto overflow-auto lg:overflow-visible;
    @media (min-width: 1024px) {
        display: block !important;
        transform: translateX(0) !important;
        transition: none !important;
    }
}
.lvl1-menu-group {
    @apply lg:w-[calc(50%-5rem)] flex flex-col lg:flex-row lg:items-center px-2 lg:px-0;
    &--left {
        @apply lg:justify-end lg:pr-[42px] xl:pr-[40px] 2xl:pr-[92px];
    }
    &--right {
        @apply lg:justify-start lg:pl-[42px] xl:pl-[40px] 2xl:pl-[92px];
    }
}

.lvl1-menu-item {
    @apply static border-b border-black lg:border-none;
    > a,
    > button {
        @apply font-Poppins font-medium text-sm text-left uppercase;
        @apply flex items-center justify-between lg:justify-start w-full lg:w-auto py-5 lg:py-6;

        i {
            @apply text-sm lg:ml-2 transition-transform;
        }
    }

    > a span,
    > button span {
        position: relative;
        &::after {
            content: '';
            display: none;
            position: absolute;
            bottom: -4px;
            left: 0;
            width: 100%;
            height: 2px;
            @apply bg-yellow-400;
        }
    }
    > a,
    button {
        span,
        i {
            pointer-events: none;
        }
    }
}

.bg-theme-dark {
    .lvl1-menu-item {
        > a,
        > button {
            @apply lg:text-white;
        }
    }
}

.lvl1-menu-item__panel {
    transition-property: max-height;
    transition-timing-function: ease-out;
    @apply max-h-0 lg:max-h-[unset];
    @apply lg:absolute lg:w-screen lg:top-16 lg:left-1/2 lg:-translate-x-1/2 lg:pt-14 lg:pb-16;
    @apply lg:opacity-0 lg:pointer-events-none overflow-hidden lg:rounded-br-4xl lg:rounded-bl-4xl;
    @apply text-black bg-gray-100 dark:text-white dark:bg-black;
    @media (min-width: 1024px) {
        max-height: unset !important;
    }
    a,
    button {
        @apply focus:outline-black;
    }
}

.dark .lvl1-menu-item__panel {
    box-shadow: 0 10px 20px 0 rgba(255, 255, 255, 0.2);
}

.transition-panels .lvl1-menu-item__panel {
    @apply lg:transition-opacity;
}

.lvl1-menu-item.open {
    i {
        @apply -rotate-180;
    }
    .lvl1-menu-item__panel {
        @apply lg:opacity-100 lg:pointer-events-auto;
    }
}

.lvl1-menu-item.open > button span,
.lvl1-menu-item:hover > button span,
.lvl1-menu-item:hover > a span,
.lvl1-menu-item > .nuxt-link-active > span {
    &::after {
        @apply lg:block;
    }
}

/* add some padding to ul's so that outlines of anchors inside won't be cut off */
.lvl1-menu-item__panel ul {
    @apply p-2 lg:p-0;
}

.lvl2-menu-item {
    @apply inline-block;
    span {
        @apply font-Poppins font-semibold text-lg hover:underline;
    }
}

.desktop-search {
    border-width: 0 0 1px 0;
    @apply border-black rounded-none pr-8 pl-0 py-1;
}
</style>
